import React, { useEffect, useRef, useCallback } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

/* ✅ FONT SIZE WHITELIST */
const Size = Quill.import("attributors/style/size");
Size.whitelist = [
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "23px",
  "26px",
  "32px",
];
Quill.register(Size, true);

/* ✅ IMAGE */
const ImageBlot = Quill.import("formats/image");

class CustomImageBlot extends ImageBlot {
  static create(value) {
    const node = super.create(value);
    node.setAttribute("src", value);

    node.style.maxWidth = "100%";
    node.style.borderRadius = "0.5rem";
    node.style.margin = "1rem 0";

    return node;
  }
}
Quill.register(CustomImageBlot, true);

/* ✅ VIDEO */
const BlockEmbed = Quill.import("blots/block/embed");

class VideoBlot extends BlockEmbed {
  static blotName = "video";
  static tagName = "video";

  static create(value) {
    const node = super.create(value);
    node.setAttribute("controls", "controls");
    node.setAttribute("src", value);
    return node;
  }

  static value(node) {
    return node.getAttribute("src");
  }
}
Quill.register(VideoBlot);

/* ✅ YOUTUBE (iframe) - supports watch / share / shorts / embed / iframe HTML.
   Separate from VideoBlot so the existing local video-upload flow stays intact. */
class YoutubeBlot extends BlockEmbed {
  static blotName = "youtube";
  static tagName = "iframe";

  static create(value) {
    const node = super.create(value);
    node.setAttribute("src", value);
    node.setAttribute("frameborder", "0");
    node.setAttribute("allowfullscreen", "true");
    node.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
    );
    node.setAttribute("class", "ql-youtube-iframe");
    return node;
  }

  static value(node) {
    return node.getAttribute("src");
  }
}
Quill.register(YoutubeBlot);

/* ✅ CUSTOM TOOLBAR ICON FOR THE YOUTUBE BUTTON */
const QuillIcons = Quill.import("ui/icons");
QuillIcons["youtube"] =
  '<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">' +
  '<rect x="1" y="4" width="16" height="10" rx="2" ry="2" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
  '<polygon points="7.5,7 7.5,11 11.5,9" fill="currentColor"/>' +
  "</svg>";

/* ✅ Helper - extract a YouTube embed URL from any supported input:
     - https://www.youtube.com/watch?v=VIDEO_ID
     - https://youtu.be/VIDEO_ID
     - https://www.youtube.com/embed/VIDEO_ID
     - https://www.youtube.com/shorts/VIDEO_ID
     - <iframe ... src="https://www.youtube.com/embed/VIDEO_ID" ...></iframe>
   Returns "" if the input isn't a recognizable YouTube reference. */
const getYoutubeEmbedUrl = (input) => {
  if (!input) return "";
  const raw = String(input);
  const iframeMatch = raw.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  const url = iframeMatch ? iframeMatch[1] : raw.trim();
  const idMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/i,
  );
  if (idMatch && idMatch[1]) {
    return "https://www.youtube.com/embed/" + idMatch[1];
  }
  return "";
};

/* ✅ Quick check whether a pasted string is a PURE YouTube reference
   (URL or full iframe). Used to decide whether to auto-embed on paste. */
const isPureYoutubeReference = (text) => {
  if (!text) return false;
  const t = String(text).trim();
  if (!t) return false;
  return (
    /^(https?:\/\/)?(www\.|m\.)?(youtube\.com|youtu\.be)\/\S+$/i.test(t) ||
    /^<iframe[^>]*src=["'][^"']*(youtube\.com|youtu\.be)/i.test(t)
  );
};

const CustomQuillEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);

  /* Inline YouTube input bar refs */
  const youtubeBarRef = useRef(null);
  const youtubeInputRef = useRef(null);
  const youtubeErrorRef = useRef(null);
  const savedRangeRef = useRef(null);

  /* ✅ IMAGE UPLOAD */
  const handleImageUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          const range = quillRef.current.getSelection(true);
          quillRef.current.insertEmbed(range.index, "image", reader.result);
          quillRef.current.setSelection(range.index + 1);
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  /* ✅ VIDEO UPLOAD */
  const handleVideoUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/*";
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          const range = quillRef.current.getSelection(true);
          quillRef.current.insertEmbed(range.index, "video", reader.result);
          quillRef.current.setSelection(range.index + 1);
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  /* ✅ Inline YouTube bar - no window.prompt / window.alert anywhere */
  const hideYoutubeBar = useCallback(() => {
    if (youtubeInputRef.current) youtubeInputRef.current.value = "";
    if (youtubeErrorRef.current) youtubeErrorRef.current.textContent = "";
    if (youtubeBarRef.current) youtubeBarRef.current.style.display = "none";
  }, []);

  const insertYoutubeFromBar = useCallback(() => {
    if (!quillRef.current || !youtubeInputRef.current) return;

    const raw = String(youtubeInputRef.current.value || "").trim();
    if (!raw) {
      if (youtubeErrorRef.current) {
        youtubeErrorRef.current.textContent = "Please paste a YouTube URL.";
      }
      return;
    }

    const embedUrl = getYoutubeEmbedUrl(raw);
    if (!embedUrl) {
      if (youtubeErrorRef.current) {
        youtubeErrorRef.current.textContent =
          "Invalid YouTube URL. Use a watch / share / shorts / embed link or iframe code.";
      }
      return;
    }

    const range =
      savedRangeRef.current || { index: quillRef.current.getLength() };
    quillRef.current.insertEmbed(range.index, "youtube", embedUrl, "user");
    quillRef.current.setSelection(range.index + 1);
    hideYoutubeBar();
  }, [hideYoutubeBar]);

  /* ✅ YOUTUBE TOOLBAR HANDLER - opens the inline input bar (no prompt) */
  const handleYoutubeEmbed = useCallback(() => {
    if (!quillRef.current) return;
    // Remember cursor position so the embed lands where the user was typing.
    savedRangeRef.current =
      quillRef.current.getSelection(true) || {
        index: quillRef.current.getLength(),
      };
    if (youtubeBarRef.current) {
      youtubeBarRef.current.style.display = "flex";
    }
    if (youtubeInputRef.current) {
      youtubeInputRef.current.value = "";
      youtubeInputRef.current.focus();
    }
    if (youtubeErrorRef.current) {
      youtubeErrorRef.current.textContent = "";
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme: "snow",
        placeholder: "Start writing...",
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ size: Size.whitelist }], // ✅ FONT SIZE
              [{ align: [] }],
              ["bold", "italic", "underline", "strike"],
              [{ color: [] }, { background: [] }], // ✅ COLORS
              [{ list: "ordered" }, { list: "bullet" }],
              ["link", "image", "video", "youtube"], // ✅ YouTube alongside video
              ["clean"],
            ],
            handlers: {
              image: handleImageUpload,
              video: handleVideoUpload,
              youtube: handleYoutubeEmbed, // ✅ Opens inline bar (no prompt)
            },
          },
        },
        formats: [
          "header",
          "size", // ✅
          "align",
          "bold",
          "italic",
          "underline",
          "strike",
          "list",
          "bullet",
          "link",
          "image",
          "video",
          "youtube", // ✅ keep YouTube embeds through the formats whitelist
          "color", // ✅
          "background", // ✅
        ],
      });

      /* ✅ STYLES */
      const styleTag = document.createElement("style");
      styleTag.innerHTML = `
        .ql-editor p {
          font-size: 16px;
          line-height: 28px;
          margin-bottom: 1rem;
        }

        .ql-editor img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 1rem 0;
        }

        .ql-editor video {
          max-width: 100%;
          display: block;
          margin: 1rem 0;
        }

        /* ✅ YouTube embed (iframe) - mirrors the .ql-editor video sizing rules */
        .ql-editor iframe.ql-youtube-iframe {
          display: block;
          width: 560px;
          max-width: 100%;
          height: 315px;
          margin: 1rem 0;
          border: 0;
          border-radius: 0.5rem;
        }

        .ql-editor a {
          color: #0066cc;
          text-decoration: underline;
          cursor: pointer;
        }
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: "12px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: "14px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: "16px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: "18px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: "20px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="23px"]::before { content: "23px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="26px"]::before { content: "26px"; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: "32px"; }

/* Label display */
.ql-snow .ql-picker.ql-size .ql-picker-label::before {
  content: attr(data-value);
}
      `;
      document.head.appendChild(styleTag);

      /* ✅ INITIAL VALUE */
      if (value) {
        quillRef.current.root.innerHTML = value;
      }

      /* ✅ CHANGE HANDLER */
      quillRef.current.on("text-change", () => {
        if (onChange) {
          onChange(quillRef.current.root.innerHTML);
        }
      });

      /* ✅ OPEN LINKS IN NEW TAB */
      quillRef.current.root.addEventListener("click", (e) => {
        if (e.target.tagName === "A") {
          e.preventDefault();
          window.open(e.target.href, "_blank");
        }
      });

      /* ✅ AUTO-CONVERT PASTED YOUTUBE URLs / IFRAME CODE
         Capture-phase listener so it runs before Quill's own clipboard
         processing. When the pasted content is a pure YouTube reference
         (URL or iframe code), insert it as an embed instead. No popups. */
      quillRef.current.root.addEventListener(
        "paste",
        (e) => {
          const cd = e.clipboardData || window.clipboardData;
          if (!cd) return;
          const text = cd.getData("text/plain") || "";
          if (!isPureYoutubeReference(text)) return;
          const embedUrl = getYoutubeEmbedUrl(text);
          if (!embedUrl) return;
          e.preventDefault();
          e.stopPropagation();
          const range =
            quillRef.current.getSelection(true) || {
              index: quillRef.current.getLength(),
            };
          quillRef.current.insertEmbed(range.index, "youtube", embedUrl, "user");
          quillRef.current.setSelection(range.index + 1);
        },
        true,
      );
    }
  }, [handleImageUpload, handleVideoUpload, handleYoutubeEmbed]);

  /* ✅ EXTERNAL VALUE UPDATE */
  useEffect(() => {
    if (quillRef.current && value !== quillRef.current.root.innerHTML) {
      quillRef.current.root.innerHTML = value;
    }
  }, [value]);

  return (
    <div style={{ position: "relative" }}>
      <div ref={editorRef} style={{ minHeight: "200px" }} />

      {/* ✅ Inline YouTube input bar - replaces window.prompt + window.alert.
          Opened by the YouTube toolbar button. Hidden by default. */}
      <div
        ref={youtubeBarRef}
        className="ql-youtube-input-bar"
        style={{
          display: "none",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          padding: "10px 12px",
          marginTop: "8px",
          background: "#f8f9fa",
          border: "1px solid #ccc",
          borderRadius: "6px",
        }}
      >
        <input
          ref={youtubeInputRef}
          type="text"
          placeholder="Paste YouTube URL (watch / share / shorts / embed) or iframe code, then press Enter…"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              insertYoutubeFromBar();
            } else if (e.key === "Escape") {
              e.preventDefault();
              hideYoutubeBar();
            }
          }}
          style={{
            flex: "1 1 280px",
            minWidth: 0,
            padding: "6px 10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "14px",
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={insertYoutubeFromBar}
          style={{
            padding: "6px 14px",
            border: "1px solid #0066cc",
            background: "#0066cc",
            color: "#fff",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Insert
        </button>
        <button
          type="button"
          onClick={hideYoutubeBar}
          style={{
            padding: "6px 14px",
            border: "1px solid #ccc",
            background: "#fff",
            color: "#333",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Cancel
        </button>
        <div
          ref={youtubeErrorRef}
          style={{
            flex: "1 1 100%",
            color: "#d32f2f",
            fontSize: "12px",
            minHeight: "14px",
          }}
        />
      </div>
    </div>
  );
};

export default CustomQuillEditor;